// What every ambient effect is made from. An effect draws on its own clear
// canvas over the picture, at a resolution that suits it (soft things draw
// small and the browser scales them up), lands on the picture with a blend
// that suits it (light adds with screen, shade darkens with multiply), and runs
// at the frame rate it needs, which for slow things is low. The standard is the
// drawn Aurora's: slow, soft, few, glowing, and quiet in the middle of the
// screen, where the tiles sit.

import { css, layer, rng, type RGB } from "../scenes";
import type { PictureMap } from "./map";

export interface EffectEnv {
  /** the canvas, in its own pixels */
  w: number;
  h: number;
  /** canvas pixels for one pixel of a 1600-wide screen, so every size follows the screen */
  u: number;
  /** 0 to 1 */
  strength: number;
  /** the color the theme asked for, when it asked */
  color: RGB | null;
  /** the picture behind it, measured, when there is one */
  map: PictureMap | null;
  seed: number;
  /** a place the theme named, 0 to 1 across and down */
  x?: number;
  y?: number;
}

export interface Effect {
  fps: number;
  /** its canvas against the screen's pixels: soft effects draw small */
  res: number;
  /** how it lands on what is under it */
  blend: "normal" | "screen" | "multiply";
  build(env: EffectEnv): unknown;
  frame(ctx: CanvasRenderingContext2D, t: number, state: unknown, env: EffectEnv): void;
}

export const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const wrap = (v: number, m: number) => ((v % m) + m) % m;
export function smooth(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}
export const mixRGB = (a: RGB, b: RGB, t: number): RGB => [Math.round(a[0] + (b[0] - a[0]) * t), Math.round(a[1] + (b[1] - a[1]) * t), Math.round(a[2] + (b[2] - a[2]) * t)];
export const lift = (c: RGB, t: number): RGB => mixRGB(c, [255, 255, 255], t);

/** How many of a thing a screen holds, against a 1600 by 900 one: counts follow the screen's area. */
export const areaOf = (env: EffectEnv) => (env.w / env.u) * (env.h / env.u) / (1600 * 900);

/**
 * 1 at the edges of the screen, falling to about 0.4 in the middle, where the
 * tiles sit: whatever moves behind the glass is blurred into it, so less moves
 * there.
 */
export function calm(x: number, y: number): number {
  const dx = (x - 0.5) / 0.34;
  const dy = (y - 0.52) / 0.32;
  return 1 - 0.6 * Math.exp(-(dx * dx + dy * dy));
}

/** A number from two whole numbers that is the same every time, for things that come back changed. */
export function hash2(a: number, b: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
}

/** A soft round light, drawn once and stamped wherever one is. core is how much of it is solid. */
export function softDot(rgb: RGB, r: number, core = 0.18): HTMLCanvasElement {
  const size = Math.max(4, Math.ceil(r * 2));
  const [c, ctx] = layer(size, size);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, css(rgb, 1));
  g.addColorStop(core, css(rgb, 0.85));
  g.addColorStop(Math.min(0.9, core + 0.27), css(rgb, 0.22));
  g.addColorStop(1, css(rgb, 0));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return c;
}

/** A light with a small white-hot heart and a wide halo of its color: a firefly, an ember, a lamp. */
export function glowDot(rgb: RGB, r: number): HTMLCanvasElement {
  const size = Math.max(6, Math.ceil(r * 2));
  const [c, ctx] = layer(size, size);
  const m = size / 2;
  const halo = ctx.createRadialGradient(m, m, 0, m, m, m);
  halo.addColorStop(0, css(rgb, 0.55));
  halo.addColorStop(0.3, css(rgb, 0.22));
  halo.addColorStop(0.65, css(rgb, 0.06));
  halo.addColorStop(1, css(rgb, 0));
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, size, size);
  const heart = ctx.createRadialGradient(m, m, 0, m, m, m * 0.22);
  const hot = lift(rgb, 0.6);
  heart.addColorStop(0, css(hot, 1));
  heart.addColorStop(0.5, css(hot, 0.7));
  heart.addColorStop(1, css(rgb, 0));
  ctx.fillStyle = heart;
  ctx.fillRect(0, 0, size, size);
  return c;
}

/** Value noise that wraps every px across and py down, so a texture made from it tiles. */
export function periodicNoise(seed: number, px: number, py: number) {
  const r = rng(seed);
  const g = Float32Array.from({ length: px * py }, () => r());
  const at = (x: number, y: number) => g[wrap(y, py) * px + wrap(x, px)];
  return (x: number, y: number) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;
    const u = fx * fx * (3 - 2 * fx);
    const v = fy * fy * (3 - 2 * fy);
    const a = at(xi, yi) + (at(xi + 1, yi) - at(xi, yi)) * u;
    const b = at(xi, yi + 1) + (at(xi + 1, yi + 1) - at(xi, yi + 1)) * u;
    return a + (b - a) * v;
  };
}

/**
 * Soft shapes from layered noise, as a texture that wraps left to right: banks
 * of mist, the shadows of clouds. cells is how many shapes fit across at the
 * coarsest scale; shape turns the noise (0 to 1) and the height down the
 * texture (0 to 1) into how solid it is there.
 */
export function noiseTexture(w: number, h: number, o: { seed: number; cells: number; rows: number; octaves?: number; rgb: RGB; shape: (n: number, fy: number) => number }): HTMLCanvasElement {
  const oct = o.octaves ?? 4;
  const ns = Array.from({ length: oct }, (_, k) => periodicNoise(o.seed + k * 131, o.cells << k, o.rows << k));
  const [c, ctx] = layer(w, h);
  const img = ctx.createImageData(w, h);
  for (let j = 0; j < h; j++) {
    const fy = j / h;
    for (let i = 0; i < w; i++) {
      let n = 0;
      let amp = 1;
      let norm = 0;
      for (let k = 0; k < oct; k++) {
        const f = 1 << k;
        n += ns[k]((i / w) * o.cells * f, fy * o.rows * f) * amp;
        norm += amp;
        amp *= 0.5;
      }
      const p = (j * w + i) * 4;
      img.data[p] = o.rgb[0];
      img.data[p + 1] = o.rgb[1];
      img.data[p + 2] = o.rgb[2];
      img.data[p + 3] = Math.round(clamp01(o.shape(n / norm, fy)) * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

/** Keep only what was drawn in the sky: light belongs behind the trees, hills and roofs, never on them. */
export function keepToSky(ctx: CanvasRenderingContext2D, mask: HTMLCanvasElement | null, w: number, h: number) {
  if (!mask) return;
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(mask, 0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";
}

/** Specks of random clearness, up to amount: what dither cuts out. */
export function speckle(size: number, amount: number): HTMLCanvasElement {
  const [c, ctx] = layer(size, size);
  const img = ctx.createImageData(size, size);
  const r = rng(77);
  for (let i = 0; i < size * size; i++) img.data[i * 4 + 3] = Math.round(r() * amount * 255);
  ctx.putImageData(img, 0, 0);
  return c;
}

/**
 * Fine grain cut out of what was drawn, as film grain sits over a photograph:
 * a smooth, faint gradient on a dark picture shows rings without it.
 */
export function dither(ctx: CanvasRenderingContext2D, grainTex: HTMLCanvasElement, w: number, h: number) {
  const pat = ctx.createPattern(grainTex, "repeat");
  if (!pat) return;
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = pat;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";
}

/** Hue turned by deg degrees, the same lightness and color: for an aurora built from one color. */
export function turnHue(c: RGB, deg: number): RGB {
  const [r, g, b] = c.map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return c;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let hh = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  hh = wrap(hh / 6 + deg / 360, 1);
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const ch = (t: number) => {
    const x = wrap(t, 1);
    return x < 1 / 6 ? p + (q - p) * 6 * x : x < 1 / 2 ? q : x < 2 / 3 ? p + (q - p) * (2 / 3 - x) * 6 : p;
  };
  return [Math.round(ch(hh + 1 / 3) * 255), Math.round(ch(hh) * 255), Math.round(ch(hh - 1 / 3) * 255)];
}
